import { createStore, Store, useStore as useVuexStore } from 'vuex'
import { IRootState, IStoreState } from '@/store/types'
import login from '@/store/login/login'
import system from '@/store/main/system/system'
import router from '@/router'

const store = createStore<IRootState>({
  state: () => {
    return {
      name: 'jstd'
    }
  },
  modules: {
    login,
    system
  }
})

/**
 * 根据路由名自动推导需要保留的列表数据前缀
 * 规则：驼峰转小写 + 智能匹配
 * 例如：CustomerChannelStatistics -> customerChannel
 *      LiveAccessStatistics -> liveAccess
 *      Employee -> employee
 */
function getKeepListFromRoute(
  routeName: string | symbol | undefined | null,
  state: any
): string[] {
  if (!routeName || typeof routeName !== 'string') {
    return []
  }

  // 将路由名转为可能的列表前缀（驼峰转小写）
  const possiblePrefixes: string[] = []

  // 规则 1: 直接转小写（如 Employee -> employee）
  possiblePrefixes.push(routeName.charAt(0).toLowerCase() + routeName.slice(1))

  // 规则 2: 移除常见后缀后再转小写
  const suffixes = ['Statistics', 'Manage', 'Library', 'Detail', 'List', 'Query', 'Record', 'Fill', 'Editor']
  let baseName = routeName
  for (const suffix of suffixes) {
    if (baseName.endsWith(suffix)) {
      baseName = baseName.slice(0, -suffix.length)
      possiblePrefixes.push(baseName.charAt(0).toLowerCase() + baseName.slice(1))
      break
    }
  }

  // 规则 3: 尝试更短的后缀匹配（如 AccessUv -> accessUv -> access）
  const shortSuffixes = ['Uv', 'Dept', 'Emp', 'Corp', 'Faq', 'H5', 'So', 'Yb']
  for (const suffix of shortSuffixes) {
    if (routeName.endsWith(suffix)) {
      const shorter = routeName.slice(0, -suffix.length)
      possiblePrefixes.push(shorter.charAt(0).toLowerCase() + shorter.slice(1))
      break
    }
  }

  // 在所有 List 字段中查找匹配的
  const allListKeys = state
    ? Object.keys(state).filter(
        (key: string) => key.endsWith('List') && Array.isArray(state[key])
      )
    : []

  for (const prefix of possiblePrefixes) {
    const listKey = `${prefix}List`
    if (allListKeys.includes(listKey)) {
      return [prefix]
    }
  }

  // 如果都没匹配到，返回空数组（不保留任何数据）
  return []
}

export function setupStore() {
  store.dispatch('login/loadLocalAction')

  // ========== 定时清理 Vuex 列表数据，防止内存累积 ==========
  // 每 30 分钟清理一次，自动保留当前页面所需的数据
  console.log('[Vuex Cleanup] 正在启动定时清理任务...')
  setInterval(() => {
    const currentRoute = router.currentRoute.value
    const keepList = getKeepListFromRoute(currentRoute?.name ?? null, (store.state as any).system)
    store.commit('system/clearListData', keepList)
    console.log('[Vuex Cleanup] 已清理列表数据，保留:', keepList.length > 0 ? keepList : '无')
  }, 30 * 60 * 1000) // 30 分钟
}

export function useStore(): Store<IStoreState> {
  return useVuexStore()
}

export default store
